import axios from 'axios'
import { STATUS_CODES, CART_BUSY_RETRY_MS } from '@/constants'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { useJwtStore } from '@/stores/jwt'
import { redactUrlCredentials } from '@/utils/sentryRedact'

// Sentry module loaded dynamically in production only
let SentryModule: typeof import('@sentry/vue') | null = null
if (import.meta.env.PROD) {
  import('@sentry/vue').then((m) => {
    SentryModule = m
  })
}

export type HandleAxiosErrorOptions = {
  logPrefix?: string
  /** Status codes that should be reported to Sentry in addition to defaults */
  reportStatuses?: number[]
  /**
   * Status codes this call site treats as an expected user condition, never
   * reported to Sentry. Needed for statuses outside the built-in 4xx set that
   * an endpoint uses deliberately. A 409 for "the guest agent isn't running"
   * or "this container hasn't started yet" is the backend answering correctly,
   * not a fault. Takes precedence over `reportStatuses`.
   */
  silentStatuses?: number[]
  /** If true, report errors that don't match a known status (default true) */
  reportDefault?: boolean
  /** Report network/timeout errors (AxiosError with no response) (default true) */
  reportNetworkErrors?: boolean
  /** Inject custom Sentry-like client (for tests) */
  sentryClient?: {
    captureException: (err: unknown) => void
    withScope?: (cb: (scope: { setTag: (k: string, v: string) => void }) => void) => void
    addBreadcrumb?: (crumb: { category?: string; message?: string; level?: 'info' | 'error' | 'warning'; data?: Record<string, unknown> }) => void
  }
  /** Inject custom console (for tests) */
  console?: Pick<Console, 'error' | 'warn' | 'info'>
  /** Optional explicit feature tag (otherwise uses withSentryContext) */
  feature?: string
  /** Optional explicit route name (otherwise try to infer) */
  routeName?: string
  /** Optional explicit user id (otherwise try to infer from JWT) */
  userId?: number | string
}

export type HandleResult = { handled: boolean; status?: number }

function statusLabel(status?: number): string {
  switch (status) {
    case 400:
      return 'Bad Request'
    case 401:
      return 'Unauthorized'
    case 403:
      return 'Forbidden'
    case 404:
      return 'Not Found'
    case 409:
      return 'Conflict'
    case 422:
      return 'Unprocessable Entity'
    case 429:
      return 'Too Many Requests'
    case 500:
      return 'Internal Server Error'
    case 502:
      return 'Bad Gateway'
    case 503:
      return 'Service Unavailable'
    case 504:
      return 'Gateway Timeout'
    default:
      return 'Axios Error'
  }
}

/**
 * User-friendly German error messages for specific HTTP status codes.
 */
export const ERROR_MESSAGES = {
  SERVICE_UNAVAILABLE: 'Der Service ist momentan nicht verfügbar. Bitte versuche es später erneut.',
  GATEWAY_ERROR: 'Der Server ist vorübergehend nicht erreichbar. Bitte versuche es später erneut.',
  IMPERSONATION_BLOCKED: 'Diese Aktion ist während einer Impersonation nicht erlaubt.',
  DOMAIN_ACCOUNT_LIMIT:
    'Diese Domain hat die maximale Anzahl an E-Mail-Adressen erreicht. Bitte lösche nicht mehr benötigte Adressen oder kontaktiere den Support.',
  RESTORE_PRICE_UNAVAILABLE:
    'Für diese Domain lässt sich derzeit kein Wiederherstellungspreis ermitteln. Bitte kontaktiere den Support.',
  NO_PAYMENT_METHOD:
    'Dafür wird eine hinterlegte Zahlungsmethode benötigt. Bitte hinterlege eine und versuche es erneut.',
  INVALID_SWITCH_TARGET: 'Dieser Tarif steht für den Namespace nicht zur Auswahl.',
  FREE_TIER_SWITCH:
    'Ein kostenloser Namespace wechselt über das Upgrade, nicht über den Tarifwechsel.',
  UNSUPPORTED_PLAN_SWITCH:
    'Dieser Wechsel ist für deinen Namespace nicht möglich. Bitte wende dich an den Support.',
  QUOTA_DOES_NOT_FIT:
    'Die aktuelle Nutzung des Namespace passt nicht in die Grenzen des neuen Tarifs. Verkleinere zuerst die Ressourcen und versuche es dann erneut.',
  UPGRADE_IN_PROGRESS:
    'Für diesen Namespace läuft gerade schon ein Tarifwechsel. Bitte versuche es gleich noch einmal.',
  RESTORE_PAYMENT_FAILED:
    'Die Zahlung für die Wiederherstellung konnte nicht abgebucht werden. Es wurde nichts berechnet und die Domain wurde nicht wiederhergestellt.',
  RESTORE_REQUIRES_PAYMENT:
    'Eine Domain dieses Vertrags ist bereits abgelaufen und kann nur noch kostenpflichtig einzeln zurückgeholt werden. Öffne dazu die Domain und starte die Wiederherstellung dort.',
  RESTORE_QUOTE_STALE:
    'Der Preis für die Wiederherstellung hat sich geändert. Bitte öffne den Dialog erneut, um den aktuellen Preis zu sehen.',
  RESTORE_IN_PROGRESS:
    'Für diese Domain läuft bereits eine Wiederherstellung. Bitte warte einen Moment.',
  RESTORE_ALREADY_DONE: 'Diese Domain wurde bereits wiederhergestellt.',
  RESTORE_FAILED_REFUNDED:
    'Die Wiederherstellung ist bei der Registry fehlgeschlagen. Der Betrag wurde bereits vollständig zurückerstattet, es wurde dir also nichts berechnet. Bitte versuche es nicht sofort erneut, sondern kontaktiere den Support.',
  RESTORE_FAILED_MANUAL:
    'Die Wiederherstellung ist fehlgeschlagen, nachdem die Zahlung bereits abgebucht wurde, und die automatische Erstattung hat nicht funktioniert. Wir haben das erfasst und kümmern uns manuell darum. Bitte versuche es nicht erneut.',
  RESTORE_BLOCKED:
    'Für diese Domain ist die Löschung auf ein festes Datum terminiert. Das lässt sich nicht automatisch zurücknehmen. Bitte kontaktiere den Support, dann klären wir das direkt mit der Registry.',
  TRANSFER_PENDING:
    'Der Transfer dieser Domain ist noch nicht abgeschlossen. Diese Änderung ist erst danach möglich.',
  GENERIC: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.'
} as const

/**
 * Check if an error is a 503 Service Unavailable error.
 */
export function isServiceUnavailable(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 503
}

/**
 * Check if an error is a gateway error (502, 503, 504).
 */
export function isGatewayError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  const status = err.response?.status
  return status === 502 || status === 503 || status === 504
}

/**
 * Check if an error is any 5xx response.
 */
export function isServerError(err: unknown): boolean {
  return axios.isAxiosError(err) && (err.response?.status ?? 0) >= 500
}

/**
 * Check if an error is a 429 Too Many Requests (rate limit) error.
 */
export function isRateLimited(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 429
}

/**
 * Check if an error is an impersonation blocked error (403 with action_blocked_during_impersonation).
 */
export function isImpersonationBlocked(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  if (err.response?.status !== 403) return false
  const data = err.response?.data as { error?: string } | undefined
  return data?.error === 'action_blocked_during_impersonation'
}

/**
 * Check if an error is a per-domain account limit (409 with the backend's
 * mailbox_limit_reached / alias_limit_reached code — both per-domain caps).
 */
export function isDomainAccountLimit(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  if (err.response?.status !== 409) return false
  const data = err.response?.data as { error?: string } | undefined
  return data?.error === 'mailbox_limit_reached' || data?.error === 'alias_limit_reached'
}

/**
 * Check if a restore was refused because the domain carries a registry
 * operation scheduled for a fixed date (409 restore_blocked_scheduled_operation).
 * The registrar offers no way to cancel one, so this needs a human.
 */
export function isRestoreBlocked(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  if (err.response?.status !== 409) return false
  const data = err.response?.data as { error?: string } | undefined
  return data?.error === 'restore_blocked_scheduled_operation'
}

/**
 * Check if a change was refused because the domain's inbound transfer has not
 * completed yet (409 transfer_pending).
 */
export function isTransferPending(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  if (err.response?.status !== 409) return false
  return getBackendErrorCode(err) === 'transfer_pending'
}

/**
 * Known refusals of the domain restore and the Kubernetes plan switch, each
 * with its own next step. Null for anything else.
 *
 * Most mean nothing was charged and nothing changed: add a payment method, fix
 * the card, reopen the dialog for a fresh price, or wait for a running switch.
 * The two restore 502 codes are the exception and mean the charge DID go
 * through, so their messages say so and tell the customer not to retry.
 * 400 is accepted for the two switch codes only, so other codes keep needing
 * their own status.
 */
export function knownRefusalMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null
  const status = err.response?.status
  if (status !== 400 && status !== 402 && status !== 409 && status !== 502) return null
  const code = (err.response?.data as { error?: string } | undefined)?.error
  if (status === 400) {
    if (code === 'invalid_switch_target') return ERROR_MESSAGES.INVALID_SWITCH_TARGET
    if (code === 'unsupported_plan_switch') return ERROR_MESSAGES.UNSUPPORTED_PLAN_SWITCH
    return null
  }
  switch (code) {
    case 'restore_price_unavailable':
      return ERROR_MESSAGES.RESTORE_PRICE_UNAVAILABLE
    case 'restore_requires_payment':
      return ERROR_MESSAGES.RESTORE_REQUIRES_PAYMENT
    case 'quote_stale':
      return ERROR_MESSAGES.RESTORE_QUOTE_STALE
    case 'restore_in_progress':
      return ERROR_MESSAGES.RESTORE_IN_PROGRESS
    case 'restore_already_completed':
      return ERROR_MESSAGES.RESTORE_ALREADY_DONE
    case 'no_payment_method':
      return ERROR_MESSAGES.NO_PAYMENT_METHOD
    case 'upgrade_in_progress':
    case 'switch_in_progress':
      return ERROR_MESSAGES.UPGRADE_IN_PROGRESS
    case 'quota_does_not_fit':
      return ERROR_MESSAGES.QUOTA_DOES_NOT_FIT
    case 'free_tier_switch':
      return ERROR_MESSAGES.FREE_TIER_SWITCH
    case 'card_declined':
    case 'insufficient_funds':
    case 'expired_card':
    case 'authentication_required':
    case 'payment_not_completed':
      return ERROR_MESSAGES.RESTORE_PAYMENT_FAILED
    // These two mean the charge went through. Telling the customer to retry
    // would have them pay a second redemption fee.
    case 'restore_failed_refunded':
      return ERROR_MESSAGES.RESTORE_FAILED_REFUNDED
    case 'restore_failed_manual_settlement':
      return ERROR_MESSAGES.RESTORE_FAILED_MANUAL
    default:
      return null
  }
}

/**
 * Get a user-friendly German error message based on the error type.
 */
export function getErrorMessage(err: unknown): string {
  if (isImpersonationBlocked(err)) return ERROR_MESSAGES.IMPERSONATION_BLOCKED
  if (isRestoreBlocked(err)) return ERROR_MESSAGES.RESTORE_BLOCKED
  if (isTransferPending(err)) return ERROR_MESSAGES.TRANSFER_PENDING
  const refusal = knownRefusalMessage(err)
  if (refusal) return refusal
  if (isDomainAccountLimit(err)) return ERROR_MESSAGES.DOMAIN_ACCOUNT_LIMIT
  if (isServiceUnavailable(err)) return ERROR_MESSAGES.SERVICE_UNAVAILABLE
  if (isGatewayError(err)) return ERROR_MESSAGES.GATEWAY_ERROR
  return ERROR_MESSAGES.GENERIC
}

/**
 * Extract the backend's human-readable `message` field from an Axios error
 * body (the API returns `{ error, message }` for many 4xx cases, e.g. a 409
 * `guest_agent_unavailable` whose message tells the user what to do). Returns
 * undefined when there is no usable string, so callers can fall back to
 * getErrorMessage(). Opt-in: getErrorMessage() stays generic by design so it
 * never surfaces raw backend text app-wide — only call sites that know the
 * endpoint returns actionable messages should prefer this.
 */
export function getBackendMessage(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined
  const data = err.response?.data as { message?: unknown } | undefined
  const message = data?.message
  return typeof message === 'string' && message.trim() !== '' ? message : undefined
}

/**
 * Extract the backend's machine-readable `error` code from an Axios error
 * body (e.g. `invalid_postcode`, `phone_already_in_use`). Callers map known
 * codes to i18n messages and fall back to their generic message otherwise.
 */
export function getBackendErrorCode(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined
  const data = err.response?.data as { error?: unknown } | undefined
  return typeof data?.error === 'string' && data.error !== '' ? data.error : undefined
}

/**
 * Read an extra context field a coded error carries alongside `error` and
 * `message` (e.g. `limit` on `max_tokens_reached`, `addon`/`tld` on
 * `addon_not_supported`). Returns undefined when absent or of another type, so
 * callers can fall back to their own default.
 */
export function getBackendErrorField<T>(err: unknown, key: string): T | undefined {
  if (!axios.isAxiosError(err)) return undefined
  const value = (err.response?.data as Record<string, unknown> | undefined)?.[key]
  return value === undefined ? undefined : (value as T)
}

/**
 * Map the backend's address-validation error codes to their i18n message
 * keys. Returns undefined for other codes so callers keep their own
 * context-specific fallback. Shared by register, account data, and contact
 * forms — extend here when the backend grows new geo codes.
 */
export function postcodeMessageKey(code: string | undefined): string | undefined {
  if (code === 'invalid_postcode') return 'validation.invalidPostcodeCountry'
  if (code === 'postcode_state_mismatch') return 'validation.postcodeStateMismatch'
  return undefined
}

/**
 * Map the hosting lifecycle's 409 codes to their i18n message keys: a site
 * suspended after its package ended refuses changes, and an ended package
 * refuses new sites. Returns undefined for anything else.
 */
export function hostingLifecycleMessageKey(err: unknown): string | undefined {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return undefined
  switch (getBackendErrorCode(err)) {
    case 'site_suspended':
      return 'dashboard.hosting.errors.siteSuspended'
    case 'package_ended':
      return 'dashboard.hosting.errors.packageEnded'
    default:
      return undefined
  }
}

/**
 * Log a 409 transfer_pending without reporting it and return the text to show,
 * or undefined for any other error, which the caller handles itself. The
 * refusal is the backend answering correctly, not a fault.
 */
export function transferPendingRefusal(err: unknown, opts: HandleAxiosErrorOptions = {}): string | undefined {
  if (!isTransferPending(err)) return undefined
  handleAxiosError(err, { ...opts, silentStatuses: [...(opts.silentStatuses ?? []), STATUS_CODES.CONFLICT] })
  return getErrorMessage(err)
}

// A VServer whose subscription ended refuses start and every change with 409.
export function isVServerServiceEnded(err: unknown): boolean {
  return getBackendErrorCode(err) === 'service_ended'
}

/**
 * Normalize Axios errors, log them, and conditionally report to Sentry.
 * Returns a unified result with handled flag and optional HTTP status.
 */
export function handleAxiosError(err: unknown, opts: HandleAxiosErrorOptions = {}): HandleResult {
  const c = opts.console ?? console
  const sentry = opts.sentryClient ?? SentryModule
  const reportStatuses = new Set<number>(opts.reportStatuses ?? [])
  const silentStatuses = new Set<number>(opts.silentStatuses ?? [])
  const reportDefault = opts.reportDefault ?? true
  const reportNetworkErrors = opts.reportNetworkErrors ?? true

  const feature = opts.feature
  const routeName = opts.routeName ?? tryGetRouteName()
  const userId = opts.userId ?? tryGetUserId()

  if (!err) return { handled: false }

  const tagAndCapture = (e: unknown) => {
    if (!sentry) return // No Sentry in development
    const doCapture = () => {
      try {
        const withScope = (sentry as any).withScope as HandleAxiosErrorOptions['sentryClient'] extends infer T ? T extends { withScope?: any } ? T['withScope'] : undefined : undefined
        if (withScope) {
          withScope((scope: { setTag: (k: string, v: string) => void }) => {
            if (routeName) scope.setTag('route_name', String(routeName))
            if (userId !== undefined && userId !== null && userId !== '') scope.setTag('user_id', String(userId))
            if (feature) scope.setTag('feature', String(feature))
          })
        }
        ;(sentry as any).captureException(e)
      } catch {
        // ignore
      }
    }
    doCapture()
  }

  const addCrumb = (category: string, message: string | undefined, data?: Record<string, unknown>, level: 'info' | 'error' | 'warning' = 'error') => {
    if (!sentry) return // No Sentry in development
    try {
      const addBreadcrumb = (sentry as any).addBreadcrumb as any
      if (addBreadcrumb) addBreadcrumb({ category, message, data, level })
    } catch {
      // ignore
    }
  }

  try {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status
      if (status) {
        const label = statusLabel(status)
        // Not toJSON(): its config holds the request body and the Authorization header.
        c.error(`${opts.logPrefix ? opts.logPrefix + ' ' : ''}${label}:`,
          { method: err.config?.method, url: err.config?.url && redactUrlCredentials(err.config.url), status }
        )

        // Add breadcrumb with minimal data (no PII)
        addCrumb('http', `${label}`, {
          method: (err as any).config?.method,
          url: (err as any).config?.url,
          status
        })

        // Default behavior in current codebase: report 500 and unknown/default cases.
        // Unknown/default means any status not explicitly handled as an expected
        // user condition: 400/401/403/404, plus 402 (payment declined — user-caused,
        // emitted by cart, object storage, registry, and mail billing endpoints).
        // Call sites can add their own expected statuses via silentStatuses,
        // which wins over everything else: only the caller knows that a 409
        // from this particular endpoint is a documented outcome.
        const nonReportStatuses = new Set([400, 401, 402, 403, 404])
        const shouldReport = status >= 500 || (!nonReportStatuses.has(status)) || reportStatuses.has(status)
        if (shouldReport && !silentStatuses.has(status)) {
          tagAndCapture(err)
        }
        return { handled: true, status }
      }

      // Axios error without response (network error, CORS, timeout)
       
      c.error(`${opts.logPrefix ? opts.logPrefix + ' ' : ''}Network/Unknown Axios Error:`, (err as any).message)
      addCrumb('network', 'Network/Unknown Axios Error', { message: (err as any).message })
      if (reportNetworkErrors) {
        tagAndCapture(err)
      }
      return { handled: true }
    }

    // Non-Axios error
     
    c.error(`${opts.logPrefix ? opts.logPrefix + ' ' : ''}Unexpected Error:`, err)
    addCrumb('error', 'Unexpected Error')
    if (reportDefault) {
      tagAndCapture(err)
    }
    return { handled: true }
  } catch {
    // As a last resort, avoid throwing from the handler
    return { handled: true }
  }
}

function tryGetRouteName(): string | undefined {
  try {
    // Lazy access to avoid requiring router in tests
    const route = (window as any)?.__VUE_ROUTER_ROUTE__ as RouteLocationNormalizedLoaded | undefined
    return route?.name?.toString()
  } catch {
    return undefined
  }
}

function tryGetUserId(): number | string | undefined {
  try {
    const jwtStore = useJwtStore()
    const jwt = jwtStore.getJwtContent
    if (jwt && (jwt as any).id !== undefined && (jwt as any).id !== null) return (jwt as any).id
    return undefined
  } catch {
    return undefined
  }
}

export function useAxiosError() {
  return { handleAxiosError, isServiceUnavailable, isGatewayError, isImpersonationBlocked, getErrorMessage, getBackendMessage, getBackendErrorCode, getBackendErrorField, ERROR_MESSAGES }
}

/** The coded reason behind a 409 from the cart endpoints, if there is one. */
export const cartConflictCode = (err: unknown): string | null => {
  const e = err as { response?: { status?: number; data?: { error?: string } } }
  if (e?.response?.status !== STATUS_CODES.CONFLICT) return null
  return e?.response?.data?.error ?? null
}

/**
 * A cart the backend has frozen because a payment is attached to it. The caller
 * carries its items into a fresh cart; `cart_paid` is deliberately not included,
 * since starting over on an already-paid cart buys the same thing twice.
 */
export const isCartLockedError = (err: unknown): boolean => cartConflictCode(err) === 'cart_locked'

/**
 * The cart row was locked by a checkout in flight. The backend fails fast here
 * precisely so the caller can come back, so this is the one conflict worth
 * retrying rather than surfacing.
 */
export const isCartBusyError = (err: unknown): boolean => cartConflictCode(err) === 'cart_busy'

/**
 * POST /cart, retrying once when the backend reports the row as contended.
 *
 * The retry is kept here rather than at the call sites so that its result still
 * reaches their own conflict handling: `cart_busy` most often resolves into
 * `cart_locked`, because the checkout that held the row commits its payment id
 * moments later, and that is precisely the case the caller forks a new cart for.
 */
export const postCartWithBusyRetry = async <T>(
  post: () => Promise<T>
): Promise<T> => {
  try {
    return await post()
  } catch (err) {
    if (!isCartBusyError(err)) throw err
    await new Promise((resolve) => setTimeout(resolve, CART_BUSY_RETRY_MS))
    return await post()
  }
}
